import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { ProgressSpinnerComponent } from '@shared/ui';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matErrorRound } from '@ng-icons/material-symbols/round';
import { HelpService } from '../../bussines/help.service';
import { HelpVideoCategoryView } from '../../defs/help.interfaces';

/**
 * Help section showing contact information and role-based video tutorials.
 * <page-account-help></page-account-help>
 */
@Component({
  selector: 'page-account-help',
  templateUrl: 'help.component.html',
  styleUrls: ['help.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, ProgressSpinnerComponent, NgIconComponent],
  providers: [provideIcons({ matErrorRound })],
})
export class PageAccountHelpComponent {
  private readonly helpService = inject(HelpService);
  readonly #route = inject(ActivatedRoute);
  readonly #injector = inject(Injector);
  readonly #fragment = toSignal(this.#route.fragment);
  #hasScrolled = false;

  /** Help configuration loaded from JSON (signal from service) */
  private readonly $config = this.helpService.config;

  /** Video categories filtered by user role */
  private readonly $videoCategories = toSignal(this.helpService.getVideosGroupedByCategory());

  /** Contacts title text */
  $contactsTitle = computed(() => this.$config()?.contactsTitle ?? '');

  /** Contact entries */
  $contacts = computed(() => this.$config()?.contacts ?? []);

  /** IDs of videos that failed to load or play */
  private readonly $erroredVideoIds = signal<ReadonlySet<string>>(new Set());

  /** Video categories with filtered videos, enriched with the error view state */
  $categories = computed<HelpVideoCategoryView[]>(() => {
    const erroredIds = this.$erroredVideoIds();
    return (this.$videoCategories() ?? []).map((category) => ({
      ...category,
      videos: category.videos.map((video) => ({
        ...video,
        hasError: erroredIds.has(video.id),
      })),
    }));
  });

  /** Loading state */
  $loading = computed(() => !this.$config());

  /**
   * Marks a video as failed so the Nextcloud fallback link is shown instead.
   * Triggered by the video element's `error` event (e.g. Firefox/Safari over VPN).
   * @param videoId - The unique identifier of the affected video
   */
  onVideoError(videoId: string): void {
    this.$erroredVideoIds.update((ids) => new Set(ids).add(videoId));
  }

  constructor() {
    effect(() => {
      const fragment = this.#fragment();
      const loading = this.$loading();

      if (!fragment || loading || this.#hasScrolled) {
        return;
      }

      this.#hasScrolled = true;
      afterNextRender(
        () => {
          document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth' });
        },
        { injector: this.#injector },
      );
    });
  }
}
